import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";

import classes from "./SlideInPanel.module.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  /** Return false to keep the panel open (e.g. validation failed). */
  onApply?: () => boolean | void | Promise<boolean | void>;
  title?: string;
  children: ReactNode;
  showActionButtons?: boolean;
  cancelButtonText?: string;
  applyButtonText?: string;
  extraButtonText?: string;
  onExtraButtonClick?: () => void;
  footerDirection?: "row" | "column";
};

export const SlideInPanel = ({
  isOpen,
  onClose,
  onCancel,
  onApply,
  title,
  children,
  showActionButtons = true,
  cancelButtonText,
  applyButtonText,
  extraButtonText,
  onExtraButtonClick,
  footerDirection = "row",
}: Props) => {
  const [isClosing, setIsClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    // Single close ownership: ignore re-entrant closes from parent + panel.
    if (isClosingRef.current) {
      return;
    }
    isClosingRef.current = true;
    setIsClosing(true);
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      onClose();
      isClosingRef.current = false;
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, 200); // Match the animation duration
  }, [onClose, clearCloseTimeout]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  };

  const handleApply = async () => {
    if (onApply) {
      const result = await onApply();
      if (result === false) {
        return;
      }
    }
    handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (panelRef.current && !panelRef.current.contains(target)) {
        // Check if the click is on a floating portal element
        // This includes FloatingUI portals, dropdown options, and other overlay elements
        const isClickOnFloatingElement =
          // Check for FloatingUI portal containers
          target.closest("[data-floating-ui-portal]") ||
          // Check for dropdown-related elements
          target.closest('[role="listbox"]') ||
          target.closest('[role="option"]') ||
          target.closest('[role="combobox"]') ||
          // Check for elements with high z-index (likely floating)
          target.closest('[style*="z-index"]') ||
          // Check for specific floating classes used by this app
          target.closest(".floating-ui-portal") ||
          // Check if target itself is a floating element
          target.getAttribute("role") === "listbox" ||
          target.getAttribute("role") === "option" ||
          target.getAttribute("role") === "combobox";

        // Don't close if clicking on floating UI elements
        if (!isClickOnFloatingElement) {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Reset closing state when panel opens; clear orphaned close timers when
  // the parent closes the panel immediately (e.g. row deselect).
  useEffect(() => {
    if (isOpen) {
      isClosingRef.current = false;
      setIsClosing(false);
      return;
    }
    clearCloseTimeout();
    isClosingRef.current = false;
    setIsClosing(false);
  }, [isOpen, clearCloseTimeout]);

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, [clearCloseTimeout]);

  if (!isOpen) {
    return null;
  }

  const panelContent = (
    <div
      className={`${classes.overlay} ${isClosing ? classes.overlayClosing : ""}`}
    >
      <div
        ref={panelRef}
        className={`${classes.slideInPanel} ${isClosing ? classes.slideInPanelClosing : ""}`}
      >
        <Card grow>
          <Card.Header withBottomPadding>
            <Card.Header.Title>{title}</Card.Header.Title>
          </Card.Header>
          <div className={classes.scrollableBody}>{children}</div>
          {showActionButtons && (
            <Card.Footer>
              <Box direction={footerDirection} justify={"space-between"} align={footerDirection === "column" ? "stretch" : "center"} gap={8} fullWidth>
                {cancelButtonText && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                    stretch={true}
                  >
                    {cancelButtonText}
                  </Button>
                )}
                {extraButtonText && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onExtraButtonClick}
                    stretch={true}
                  >
                    {extraButtonText}
                  </Button>
                )}
                {applyButtonText && (
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleApply}
                    stretch={true}
                  >
                    {applyButtonText}
                  </Button>
                )}
              </Box>
            </Card.Footer>
          )}
        </Card>
      </div>
    </div>
  );

  return createPortal(panelContent, document.body);
};
